import cn from 'classnames';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/form';

import { useCommon } from 'hooks/useCommon';
import { API_ROUTES } from 'lib/api-routes';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { TUserApiResponse } from 'lib/models/User.model';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';

import Header from '../Header';
import StepHeading from '../StepHeading';
import styles from './Step1.module.scss';

interface Step1Props {
  progress: number;
  setFlowState: (flowState: string) => void;
}

export const Step1: React.FC<Step1Props> = ({ progress, setFlowState }) => {
  const { managerSociety, updateUserApiResponse, manager } = useAuth();
  const [t] = useTranslation('onboarding.manager');

  const { onShare } = useCommon();

  const [isCompleted, setIsCompleted] = React.useState(false);

  const [isEventExist, setIsEventExist] = React.useState(false);

  const show_custom_link_screen =
    manager?.linktree_url !== null ||
    managerSociety?.custom_links?.data.length === 0;

  useEffect(() => {
    if (managerSociety === null) return;
    const {
      instagram_url,
      facebook_url,
      linkedin_url,
      website,
      student_union_url,
      short_description,
      description,
      banner_url,
      custom_links,
      primary_topic,
      managers,
      stats,
      offerings,
      student_societies_highlights,
      flagship_events,
    } = managerSociety;

    setIsCompleted(
      !!banner_url &&
        !!description &&
        !!short_description &&
        !!custom_links?.data.length &&
        !!(
          instagram_url ||
          facebook_url ||
          linkedin_url ||
          website ||
          student_union_url
        ) &&
        !!primary_topic &&
        !!stats?.data.length &&
        !!managers?.data.length &&
        !!offerings?.data.length &&
        !!student_societies_highlights?.data.length
    );

    setIsEventExist(!!flagship_events?.data.length);
  }, [managerSociety]);

  const handleNext = async () => {
    let flow_state;
    if (show_custom_link_screen) {
      flow_state = ONBOARDING_FLOW_STATE.POST_2;
    } else {
      flow_state = isCompleted
        ? isEventExist
          ? ONBOARDING_FLOW_STATE.COMPLETE
          : ONBOARDING_FLOW_STATE.POST_4
        : ONBOARDING_FLOW_STATE.POST_3;
    }
    const manager = { flow_state };
    const userResponse = await apiInstance.patch<TUserApiResponse>(
      API_ROUTES.MANAGER_USER,
      {
        manager,
      }
    );

    updateUserApiResponse(userResponse.data);
    setFlowState(flow_state);
  };

  return (
    <>
      <Header
        percentComplete={progress}
        onBack={() => {
          setFlowState(ONBOARDING_FLOW_STATE.PRE_COMPLETE);
        }}
      />
      <div className={styles.stepContent}>
        <StepHeading
          title={t('step-1.header.title')}
          desc={t('step-1.header.desc')}
        />

        <div>
          <Button
            type="button"
            color="primary"
            className={styles.button}
            onClick={() => {
              onShare(
                t('step-1.button.share.text'),
                `${window.location.origin}${PAGE_ROUTES.MANAGER_SIGN_UP}`,
                {
                  title: t('step-1.button.share.title'),
                  message: t('step-1.button.share.message'),
                }
              );
            }}
            isFullWidth
          >
            {t('step-1.button.title')}
          </Button>
          <Button
            type="button"
            color="secondary"
            className={cn(styles.yesNoButton, styles.button)}
            onClick={handleNext}
            isFullWidth
          >
            {t('next')}
          </Button>
        </div>
      </div>
    </>
  );
};
